import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const fileName = (r, fallback) =>
  (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || fallback;

// Platform Admin viewer for the Core Regression Test Script (PDF), with PDF + Word downloads.
export default function RegressionScriptModal({ onClose }) {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let url;
    axios.get(`${API}/admin/regression-script/pdf`, { responseType: "blob" })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        setPdf({ url, blob: r.data, name: fileName(r, "uReserve_Regression_Test_Plan.pdf") });
      })
      .catch(() => setError(true));
    return () => url && URL.revokeObjectURL(url);
  }, []);

  const downloadWord = async () => {
    try {
      const r = await axios.get(`${API}/admin/regression-script`, { responseType: "blob" });
      saveBlob(r.data, fileName(r, "uReserve_Regression_Test_Plan.docx"));
    } catch {
      toast.error("Couldn't download the Word file.");
    }
  };

  const btn = "inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-[3px] transition-colors";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose} data-testid="regression-script-modal">
      <div className="bg-white rounded-[3px] shadow-xl flex flex-col w-full max-w-[1200px] h-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2" style={{ background: "#1C4E80", color: "#fff" }}>
          <span className="font-display font-bold text-[15px]">Core Regression Test Plan</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!pdf}
              onClick={() => saveBlob(pdf.blob, pdf.name)}
              className={`${btn} bg-white text-navy hover:bg-navy-wash disabled:opacity-50`}
              data-testid="regression-script-download-pdf"
            >
              <Download size={12} /> Download PDF
            </button>
            <button
              type="button"
              onClick={downloadWord}
              className={`${btn} text-white hover:bg-white/[0.12]`}
              style={{ border: "1px solid rgba(255,255,255,0.55)" }}
              data-testid="regression-script-download-word"
            >
              <Download size={12} /> Word
            </button>
            <button type="button" onClick={onClose} className="p-1 hover:bg-white/[0.12] rounded-[3px]" data-testid="regression-script-close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-[#EEF2F6]">
          {pdf ? (
            <iframe title="Regression Test Plan" src={pdf.url} className="w-full h-full border-0" data-testid="regression-script-viewer" />
          ) : (
            <div className="h-full flex items-center justify-center text-[12px] text-slate gap-2" data-testid="regression-script-status">
              {error ? "Couldn't load the test script PDF." : (<><Loader2 size={14} className="animate-spin" /> Preparing PDF…</>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
